/**
 * Eigentümer-Portal — Beschluss-Sammlung (§ 24 Abs. 7 WEG).
 *
 * Zeigt alle Beschlüsse aller WEG-Objekte, in denen der eingeloggte Eigentümer
 * mindestens eine Einheit besitzt. Read-only, sortiert nach Datum (neueste zuerst).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Gavel, CheckCircle, XCircle, Clock, Warning, Info } from "@phosphor-icons/react";

const OUTCOME_META = {
  angenommen:     { label: "Angenommen",    color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  abgelehnt:      { label: "Abgelehnt",     color: "bg-rose-100 text-rose-800",       icon: XCircle },
  vertagt:        { label: "Vertagt",       color: "bg-amber-100 text-amber-800",     icon: Clock },
  zurueckgezogen: { label: "Zurückgezogen", color: "bg-slate-100 text-slate-700",     icon: XCircle },
};
const CHALLENGE_LABEL = {
  angefochten: "Angefochten",
  aufgehoben: "Gerichtlich aufgehoben",
  bestaetigt: "Gerichtlich bestätigt",
  erledigt: "Erledigt",
};

export default function OwnerBeschluesse() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/weg/portal/my-resolutions")
      .then((r) => setRows(r.data?.resolutions || []))
      .finally(() => setLoading(false));
  }, []);

  // Nach Objekt gruppieren
  const grouped = rows.reduce((acc, r) => {
    const key = r.property?.property_number || r.property_id;
    if (!acc[key]) acc[key] = { property: r.property, resolutions: [] };
    acc[key].resolutions.push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="owner-beschluesse-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Gavel weight="duotone" className="text-emerald-700" /> Beschluss-Sammlung
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Alle Beschlüsse der Eigentümergemeinschaften, an denen Sie beteiligt sind (§ 24 Abs. 7 WEG).
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Lade …</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center bg-slate-50">
          <Gavel size={40} className="mx-auto text-slate-400 mb-3" />
          <div className="text-sm text-slate-600">Noch keine Beschlüsse vorhanden.</div>
        </div>
      ) : (
        Object.entries(grouped).map(([key, { property, resolutions }]) => (
          <section key={key} className="space-y-3" data-testid={`owner-beschluesse-group-${key}`}>
            <h2 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1">
              {property?.name} <span className="font-mono text-xs text-slate-500 ml-2">{property?.property_number}</span>
            </h2>
            <ul className="space-y-2">
              {resolutions.map((r) => {
                const outcome = OUTCOME_META[r.outcome] || OUTCOME_META.angenommen;
                const OutcomeIcon = outcome.icon;
                return (
                  <li
                    key={r.id}
                    data-testid={`owner-beschluss-${r.resolution_number}`}
                    className={`rounded-lg border p-3 ${r.invalid ? "border-rose-300 bg-rose-50/40" : "border-slate-200 bg-white"}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-[11px] text-slate-500 font-semibold">Nr. {r.resolution_number}</span>
                      <span className="text-[11px] text-slate-500">{r.resolution_date}</span>
                      {r.agenda_item && <Badge variant="outline" className="text-[10px]">{r.agenda_item}</Badge>}
                      <Badge className={`text-[10px] ${outcome.color}`}>
                        <OutcomeIcon size={10} className="inline mr-0.5" weight="fill" /> {outcome.label}
                      </Badge>
                      {r.invalid && (
                        <Badge className="bg-rose-100 text-rose-800 text-[10px]">
                          <Warning size={10} weight="fill" className="inline mr-0.5" /> Ungültig
                        </Badge>
                      )}
                      {r.challenge_status !== "keine" && (
                        <Badge className="bg-amber-100 text-amber-800 text-[10px]">
                          {CHALLENGE_LABEL[r.challenge_status] || r.challenge_status}
                        </Badge>
                      )}
                    </div>
                    <div className="font-medium text-slate-900 text-sm">{r.title}</div>
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.text}</p>
                    {(r.votes_yes !== null || r.votes_no !== null) && (
                      <div className="text-[11px] text-slate-500 mt-2">
                        Ja: <b>{r.votes_yes ?? 0}</b> · Nein: <b>{r.votes_no ?? 0}</b> · Enthaltung: <b>{r.votes_abstain ?? 0}</b>
                      </div>
                    )}
                    {r.challenge_deadline && (
                      <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                        <Clock size={10} /> Anfechtungsfrist bis <b>{r.challenge_deadline}</b>
                        {r.challenge_period_open && <span className="text-amber-700 ml-1">(noch offen)</span>}
                      </div>
                    )}
                    {r.invalid && r.invalid_reason && (
                      <div className="mt-2 text-[11px] text-rose-700 border-l-2 border-rose-400 pl-2">
                        <Info size={10} className="inline mr-1" /> {r.invalid_reason}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
